import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { ensureTitleLimit, ensureDescriptionLimit } from "../lib/seoWidth";

export default function SEO({ title, description, project, image, keywords }) {
  const location = useLocation();
  const { lang, getEquivalentPath } = useLanguage();

  const origin = window.location.origin || "https://cphcollective.dk";
  const currentPath = location.pathname;

  // Generate canonical URL
  const canonicalUrl = `${origin}${currentPath}`;

  // Generate hreflang URLs
  const daPath = getEquivalentPath(currentPath, "da");
  const enPath = getEquivalentPath(currentPath, "en");

  const hreflangDa = `${origin}${daPath}`;
  const hreflangEn = `${origin}${enPath}`;
  const hreflangDefault = `${origin}${daPath}`; // Danish is primary standard language

  // Default SEO values if not provided (strictly <= 512px title and <= 970px description)
  const defaultTitles = {
    da: "CPH Collective | Totalentreprise & Tømrer",
    en: "CPH Collective | Turnkey & Carpentry",
  };

  const defaultDescriptions = {
    da: "CPH Collective leverer totalentreprise, projektering og tømrerarbejde i København og på Sjælland. Få et uforpligtende tilbud.",
    en: "CPH Collective provides turnkey solutions, building engineering, and carpentry services for clients in Copenhagen and Zealand.",
  };

  const defaultImage = "https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-arkitektur-totalentreprise-koebenhavn.jpg";

  // Determine final title and description
  let rawTitle = title || defaultTitles[lang];
  let rawDescription = description || defaultDescriptions[lang];
  let finalImage = image || defaultImage;

  if (project) {
    const pTitle = typeof project.title === "object" ? project.title[lang] : project.title;
    const pType = typeof project.project_type === "object" ? project.project_type[lang] : project.project_type;
    const pLoc = typeof project.location === "object" ? project.location[lang] : project.location;

    if (project.images && project.images.length > 0) {
      finalImage = project.images[0];
    }

    if (lang === "da") {
      rawTitle = `${pTitle} | Referencer | CPH Collective`;
      rawDescription = `Se ${pTitle} – ${pType} i ${pLoc}. Udført af CPH Collective med fokus på håndværk og kvalitet.`;
    } else {
      rawTitle = `${pTitle} | References | CPH Collective`;
      rawDescription = `See ${pTitle} – ${pType} in ${pLoc}. Delivered by CPH Collective with engineering excellence.`;
    }
  }

  // Strictly enforce max 512px for title (Arial 20px) and max 970px for description (Arial 14px)
  const finalTitle = ensureTitleLimit(rawTitle, 512);
  const finalDescription = ensureDescriptionLimit(rawDescription, 970);

  const defaultKeywords = {
    da: "totalentreprise, tømrerarbejde, byggeprojekt, projektering af byggeri, byggepladsservice, renovering, København, Sjælland",
    en: "turnkey construction, carpentry, construction projects, building engineering, site services, renovation, Copenhagen, Denmark",
  };
  const finalKeywords = keywords || defaultKeywords[lang];

  return (
    <Helmet>
      {/* Language attribute */}
      <html lang={lang} />

      {/* Title */}
      <title>{finalTitle}</title>

      {/* Meta tags */}
      <meta name="description" content={finalDescription} />
      {finalKeywords && <meta name="keywords" content={finalKeywords} />}
      <meta property="og:site_name" content="CPH Collective" />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:locale" content={lang === "da" ? "da_DK" : "en_US"} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />

      {/* Canonical link */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang tags */}
      <link rel="alternate" hrefLang="da" href={hreflangDa} />
      <link rel="alternate" hrefLang="en" href={hreflangEn} />
      <link rel="alternate" hrefLang="x-default" href={hreflangDefault} />
    </Helmet>
  );
}
